import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getModuleTopbarColor } from './AppSidebar';
import { departementCourant, peutModifier } from '../services/droits';
import { etudiantsAPI } from '../services/api';
import PhotosScodoc from './PhotosScodoc';
import { exporterListe } from '../services/etudiantsExport';
import EtudiantsParametrage from './EtudiantsParametrage';
import ImportApogee from './ImportApogee';
import ImportScodoc from './ImportScodoc';
import ImportScodocEtudiants from './ImportScodocEtudiants';
import EtudiantsListe from './EtudiantsListe';
import EtudiantFiche from './EtudiantFiche';
import './Etudiants.css';

/**
 * MODULE ÉTUDIANTS — coquille des quatre onglets
 * ==============================================
 * Spec     : doc/modules/etudiants.md
 * Maquette : doc/maquettes/etudiants_maquette_v5.html
 *
 * Ce fichier porte la structure des quatre onglets, la charte du module et le
 * séparateur redimensionnable de l'onglet 1. Le contenu de chaque onglet vit dans son
 * propre composant, livré à son lot :
 *   onglet 1  Étudiants          lot 1.3 — `EtudiantsListe.js` + `EtudiantFiche.js` ✅
 *   onglet 2  Imports            lot 1.2 — `ImportApogee.js` ✅ · lot 1.4 — `ImportScodoc.js` ✅
 *                                (le cadre à deux colonnes est porté ici, pas par l'un des deux)
 *   onglet 3  Photos SCODOC      lot 1.5 ✅ (trombinoscope + import .zip)
 *   onglet 4  Paramétrage        lot 1.1 — `EtudiantsParametrage.js` ✅
 *
 * ── POURQUOI LES FILTRES ET L'APPEL RÉSEAU DE L'ONGLET 1 VIVENT ICI ──────────
 * La barre de filtres est AU-DESSUS du séparateur (maquette), donc en dehors des deux
 * panneaux ; et le badge de comptage de la topbar affiche le même nombre que la carte
 * de la liste. Trois endroits ont besoin du même état : les filtres, la liste, et la
 * topbar. Le faire descendre dans la liste aurait obligé à le faire remonter aussitôt,
 * et deux propriétaires du même nombre en font toujours deux nombres.
 *
 * Pourquoi une coquille est un lot à part : l'écran `/etudiants` existait déjà en
 * production, avec un placeholder « En cours de développement ». La règle 5 du
 * protocole veut qu'un écran déjà visible passe à la maquette dans la tranche en
 * cours — pas « plus tard », sinon il est réputé fait et n'y revient jamais.
 *
 * Trois points de forme viennent de la maquette et non de la spec :
 *   · les bornes du séparateur (20 % à 55 %) et la mémorisation de sa largeur ;
 *   · le badge de comptage sur l'onglet « Étudiants » (livré au lot 1.3) et le bouton
 *     « Exporter » placé DANS la topbar, la spec le mettant en bas de l'onglet 1 ;
 *   · le libellé de l'onglet 2. ⚠️ La maquette écrit « Import APOGEE », et le lot 1.2
 *     l'avait gardé tel quel alors que la moitié droite était encore une carte
 *     d'attente. **Corrigé au lot 1.4** (écart E40) : la colonne SCODOC est livrée, le
 *     libellé annonçait donc un seul des deux imports, et l'onglet Paramétrage est
 *     l'endroit où l'on cherche un réglage SCODOC quand l'onglet qui le contient porte
 *     le nom de l'autre source. Le tableau des onglets de la spec dit « Imports » ;
 *     c'est ce nom qui est repris. Trouvé à la relecture À L'IMAGE.
 *
 * Écart de charte assumé (règle 6, écrit dans la spec §écarts) : la maquette utilise
 * des emoji dans le menu déroulant du statut, dans les en-têtes d'historique et sur
 * le bouton « Enregistré ». Ils sont remplacés par des glyphes FontAwesome.
 */

const ONGLETS = [
    { cle: 'etudiants', libelle: 'Étudiants',      icone: 'fa-users' },
    { cle: 'import',    libelle: 'Imports',        icone: 'fa-file-import' },
    { cle: 'photos',    libelle: 'Photos SCODOC',  icone: 'fa-camera' },
    { cle: 'param',     libelle: 'Paramétrage',    icone: 'fa-sliders' }
];

// Bornes du séparateur, en proportion de la largeur du panneau. Valeurs de la maquette.
// Les onglets qui ont besoin de la liste des étudiants. L'onglet 1 l'affiche, et
// l'onglet 3 en tire son trombinoscope — c'est ce qui réalise « la grille suit les
// filtres de l'onglet 1 » (spec) sans une seconde lecture du même périmètre.
// ⚠️ Un onglet oublié ici affiche une grille VIDE sans erreur : ajouter un écran qui
// lit la liste, c'est ajouter son nom ici.
const ONGLETS_QUI_LISENT_LA_LISTE = ['etudiants', 'photos'];

const LARGEUR_MIN = 0.20;
const LARGEUR_MAX = 0.55;

/** Clé de mémorisation propre à l'utilisateur : deux comptes sur le même poste ne
 *  doivent pas se marcher dessus (la spec demande « par utilisateur connecté »). */
const cleMemo = (suffixe, user) => `etudiants.${suffixe}.${user?.id || 'anonyme'}`;

/**
 * Délai avant qu'une frappe dans la recherche ne devienne une requête. La spec veut un
 * filtrage « en temps réel » ; une requête par caractère en ferait dix pour « LASSOUED »,
 * et la réponse de la troisième pourrait arriver après celle de la cinquième.
 */
const DELAI_RECHERCHE_MS = 250;

const FILTRES_VIDES = {
    sousNiveauId: '', regime: '', statut: '', boursier: '', parcours: '', recherche: ''
};

function Etudiants({ config, user, anneeId, annees }) {
    const couleurTopbar = getModuleTopbarColor('etudiants');
    const departementId = departementCourant(user, config);
    // Le niveau du profil sur CE module. Le serveur le revérifie de toute façon (un
    // PUT exige « modifier », et un refus est un 403) : ici, il ne sert qu'à ne pas
    // proposer un geste qui sera refusé.
    const modifiable = peutModifier(user, departementId, 'etudiants');

    // ── Onglet actif, restauré d'une visite à l'autre ────────────────────────
    const [onglet, setOnglet] = useState(() => {
        const memorise = localStorage.getItem(cleMemo('onglet', user));
        return ONGLETS.some((o) => o.cle === memorise) ? memorise : 'etudiants';
    });

    useEffect(() => {
        localStorage.setItem(cleMemo('onglet', user), onglet);
    }, [onglet, user]);

    // ── Séparateur liste / fiche ─────────────────────────────────────────────
    const refSplit   = useRef(null);
    const refListe   = useRef(null);
    const refResizer = useRef(null);
    const glissement = useRef(null);

    // Largeur restaurée au montage, si elle a été mémorisée.
    useEffect(() => {
        const memorisee = localStorage.getItem(cleMemo('largeurListe', user));
        if (memorisee && refListe.current) refListe.current.style.width = memorisee;
    }, [user, onglet]);

    const demarrerGlissement = useCallback((e) => {
        if (!refListe.current) return;
        glissement.current = { x: e.clientX, largeur: refListe.current.offsetWidth };
        refResizer.current?.classList.add('actif');
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
    }, []);

    useEffect(() => {
        const deplacer = (e) => {
            if (!glissement.current || !refSplit.current || !refListe.current) return;
            const total = refSplit.current.offsetWidth;
            const voulue = glissement.current.largeur + (e.clientX - glissement.current.x);
            const bornee = Math.min(Math.max(voulue, total * LARGEUR_MIN), total * LARGEUR_MAX);
            refListe.current.style.width = bornee + 'px';
        };
        const relacher = () => {
            if (!glissement.current) return;
            glissement.current = null;
            refResizer.current?.classList.remove('actif');
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            if (refListe.current) {
                localStorage.setItem(cleMemo('largeurListe', user), refListe.current.style.width);
            }
        };
        document.addEventListener('mousemove', deplacer);
        document.addEventListener('mouseup', relacher);
        return () => {
            document.removeEventListener('mousemove', deplacer);
            document.removeEventListener('mouseup', relacher);
            // Si le composant disparaît pendant un glissement, le curseur et la
            // sélection resteraient bloqués pour toute l'application.
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [user]);

    // Le séparateur est un bouton : les flèches doivent le déplacer aussi, sinon il
    // n'existe pas pour qui n'utilise pas la souris.
    const clavier = (e) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        if (!refSplit.current || !refListe.current) return;
        const total = refSplit.current.offsetWidth;
        const pas = e.key === 'ArrowLeft' ? -24 : 24;
        const bornee = Math.min(
            Math.max(refListe.current.offsetWidth + pas, total * LARGEUR_MIN),
            total * LARGEUR_MAX
        );
        refListe.current.style.width = bornee + 'px';
        localStorage.setItem(cleMemo('largeurListe', user), refListe.current.style.width);
    };

    const anneeCourante = (annees || []).find((a) => a.id === anneeId);
    const libelleAnnee  = anneeCourante?.libelle || '';

    // ═════════════════════════════════════════════════════════════════════════
    // ONGLET 1 — les filtres, la liste, la sélection (lot 1.3)
    // ═════════════════════════════════════════════════════════════════════════

    // L'année du FILTRE part de celle de l'application, et vit sa vie ensuite :
    // regarder la promotion de l'an dernier ne doit pas changer l'année de travail
    // du Planning et de l'EDT.
    const [anneeFiltre, setAnneeFiltre] = useState(anneeId || '');
    const [filtres, setFiltres] = useState(FILTRES_VIDES);
    const [rechercheSaisie, setRechercheSaisie] = useState('');
    const [liste, setListe] = useState(null);
    const [chargementListe, setChargementListe] = useState(false);
    const [erreurListe, setErreurListe] = useState(null);
    const [selection, setSelection] = useState(null);

    /**
     * ── LE GARDE-FOU DES MODIFICATIONS NON ENREGISTRÉES (lot 1.3 bis) ────────
     * La fiche expose ici deux choses : « suis-je modifiée ? » et « enregistre ». Le
     * garde-fou vit dans la COQUILLE et non dans la fiche, parce que les gestes qui
     * font perdre une saisie ne sont pas des gestes de la fiche : cliquer un autre
     * étudiant, changer d'année.
     *
     * Changer d'ONGLET ne perd rien : les quatre panneaux restent montés, la fiche
     * n'est pas démontée, et la saisie survit. Elle n'est donc pas dans la liste.
     */
    const refFiche = useRef(null);
    const [navigationEnAttente, setNavigationEnAttente] = useState(null);

    /** Exécute le geste, ou ouvre la modale si la fiche porte une saisie non enregistrée. */
    const demanderNavigation = useCallback((geste) => {
        if (refFiche.current?.modifie) { setNavigationEnAttente(() => geste); return; }
        geste();
    }, []);

    // L'année de l'application change (bandeau d'année, autre onglet) : le filtre suit,
    // tant que l'utilisateur ne l'a pas déplacé lui-même.
    useEffect(() => { setAnneeFiltre((p) => (p ? p : anneeId || '')); }, [anneeId]);

    // La frappe est retenue avant de devenir une requête.
    useEffect(() => {
        const t = setTimeout(() => {
            setFiltres((p) => (p.recherche === rechercheSaisie ? p : { ...p, recherche: rechercheSaisie }));
        }, DELAI_RECHERCHE_MS);
        return () => clearTimeout(t);
    }, [rechercheSaisie]);

    const chargerListe = useCallback(async () => {
        if (!departementId) {
            setErreurListe('Aucun département sélectionné');
            setChargementListe(false);
            return;
        }
        setChargementListe(true);
        setErreurListe(null);
        try {
            const { data } = await etudiantsAPI.getListe(departementId, {
                anneeId: anneeFiltre || undefined, ...filtres
            });
            setListe(data);
            // La sélection ne survit pas à un filtre qui la fait disparaître : une fiche
            // ouverte sur une ligne absente de la liste laisserait croire à un défaut.
            //
            // ⚠️ SAUF si la fiche porte une saisie non enregistrée (lot 1.3 bis) : la
            // fermer d'office effacerait un travail que personne n'a demandé à jeter.
            // Elle reste alors ouverte, et le prochain geste de navigation passera par
            // la modale — qui, elle, laisse le choix.
            setSelection((p) => {
                if (!p) return p;
                if (data.etudiants.some((x) => x.id === p)) return p;
                return refFiche.current?.modifie ? p : null;
            });
        } catch (e) {
            // `liste` est CONSERVÉE : elle porte les menus déroulants des filtres, et
            // les faire disparaître au premier refus empêcherait de corriger le filtre
            // fautif.
            setErreurListe(e?.response?.data?.error || e?.message || 'Liste illisible');
        } finally {
            setChargementListe(false);
        }
    }, [departementId, anneeFiltre, filtres]);

    // Chargée à l'ouverture de l'onglet, puis à chaque changement de filtre. Les quatre
    // panneaux sont montés en permanence : interroger le serveur pour un onglet que
    // l'utilisateur n'ouvrira peut-être jamais est un appel pour rien. Y revenir
    // recharge — un import a pu passer entre-temps.
    //
    // ⚠️ DEUX ONGLETS lisent cette liste depuis le lot 1.5, et le trouver a demandé
    // la relecture à l'image : le trombinoscope affichait « 0 / 0 » et un périmètre
    // vide, sans la moindre erreur à l'écran ni dans la console. Le composant recevait
    // bien la liste — elle n'avait simplement jamais été chargée, parce que la garde
    // ci-dessous ne nommait que l'onglet 1. Un écran vide qui ne dit pas pourquoi se
    // lit comme « il n'y a personne », pas comme « je n'ai rien demandé ».
    useEffect(() => {
        if (!ONGLETS_QUI_LISENT_LA_LISTE.includes(onglet)) return;
        chargerListe();
    }, [onglet, chargerListe]);

    const reinitialiserFiltres = useCallback(() => {
        setFiltres(FILTRES_VIDES);
        setRechercheSaisie('');
    }, []);

    const filtresActifs = !!(filtres.sousNiveauId || filtres.regime || filtres.statut
        || filtres.boursier || filtres.parcours || filtres.recherche);

    // ── L EXPORT DE LA LISTE FILTREE (lot 1.6 bis, ecart E7) ─────────────────
    // ⚠️ Il exporte ce que l'ecran TIENT, pas ce qu'il pourrait relire : une
    // seconde lecture du serveur pourrait rendre autre chose que l'affichage, et
    // un export qui ne correspond pas a l'ecran est pire qu'une absence d'export.
    const [exportEnCours, setExportEnCours] = useState(false);
    const lignesExportables = erreurListe ? [] : (liste?.etudiants || []);

    const exporter = useCallback(() => {
        if (!lignesExportables.length) return;
        setExportEnCours(true);
        try {
            const formation = (liste?.formations || [])
                .find((f) => String(f.id) === String(filtres.sousNiveauId));
            exporterListe(lignesExportables, {
                annee: liste?.annee?.libelle,
                formation: formation ? formation.nom : null,
                filtresActifs
            });
        } catch (e) {
            // ⚠️ Un echec d'export ne doit pas passer pour un clic sans effet : on le
            // DIT dans le meme bandeau que les erreurs de liste, seul endroit que
            // l'utilisateur regarde deja.
            setErreurListe("L'export a echoue : " + (e.message || 'erreur inconnue'));
        } finally {
            setExportEnCours(false);
        }
    }, [lignesExportables, liste, filtres.sousNiveauId, filtresActifs]);

    // Les menus déroulants viennent du serveur (années, formations, régimes, statuts) :
    // l'écran n'en tient aucune copie. Avant la première réponse, l'année retombe sur
    // la liste que l'application a déjà chargée, pour que la barre ne soit pas vide.
    const anneesFiltre = useMemo(
        () => liste?.annees || (annees || []).map((a) => ({ ...a, nb_inscrits: null })),
        [liste, annees]
    );

    return (
        <div className="etu-root">

            {/* ── TOPBAR ─────────────────────────────────────────────────── */}
            <div
                className="etu-topbar"
                style={{ background: `linear-gradient(135deg, ${couleurTopbar} 0%, ${couleurTopbar}cc 100%)` }}
            >
                <div className="etu-topbar-left">
                    <i className="fa-solid fa-user-graduate etu-topbar-icon" />
                    <div>
                        <div className="etu-topbar-title">Étudiants</div>
                        <div className="etu-topbar-sub">
                            {config?.departement?.nom || config?.nom || 'Département'}
                            {libelleAnnee ? ' · ' + libelleAnnee : ''}
                        </div>
                    </div>
                </div>

                <div className="etu-topbar-tabs">
                    {ONGLETS.map((o) => (
                        <button
                            key={o.cle}
                            className={'etu-topbar-tab' + (onglet === o.cle ? ' active' : '')}
                            onClick={() => setOnglet(o.cle)}
                            type="button"
                        >
                            <i className={'fa-solid ' + o.icone} /> {o.libelle}
                            {/* Le badge de comptage de la maquette (écart E7) : il n'apparaît
                                qu'une fois la liste lue, et jamais à zéro — un compteur qui
                                affiche « 0 » avant sa première réponse est plus trompeur
                                qu'un compteur absent. */}
                            {o.cle === 'etudiants' && liste?.total_annee > 0 && (
                                <span className="etu-topbar-badge">{liste.total_annee}</span>
                            )}
                        </button>
                    ))}
                </div>

                <div className="etu-topbar-actions">
                    {/* Écart E7, refermé au lot 1.6 bis. Le bouton était livré grisé
                        depuis le lot 1.0 ; il exporte désormais la liste FILTRÉE.

                        ⚠️ Il exporte les lignes que l'écran TIENT DÉJÀ, sans relire le
                        serveur : une seconde lecture pourrait rendre autre chose que ce
                        qui est affiché, et un export qui ne correspond pas à l'écran est
                        pire qu'une absence d'export.

                        ⚠️ Il est grisé quand il n'y a RIEN à exporter, avec sa raison —
                        un bouton qui produit un classeur vide se lit comme une panne. */}
                    <button
                        className="etu-btn-topbar"
                        type="button"
                        disabled={exportEnCours || !lignesExportables.length}
                        onClick={exporter}
                        title={!lignesExportables.length
                            ? "Rien à exporter : aucune ligne dans la liste."
                            : `Exporter les ${lignesExportables.length} ligne(s) affichées, `
                              + 'filtres compris, au format Excel.'}
                    >
                        {exportEnCours
                            ? <><i className="fa-solid fa-circle-notch etu-tourne" /> Export…</>
                            : <><i className="fa-solid fa-file-excel" /> Exporter</>}
                    </button>
                </div>
            </div>

            {/* ── CONTENU ────────────────────────────────────────────────── */}
            <div className="etu-content">

                {/* ONGLET 1 — ÉTUDIANTS (lot 1.3, en lecture) */}
                <div className={'etu-panneau' + (onglet === 'etudiants' ? ' active' : '')}>

                    {/* ── Les six filtres de la maquette ─────────────────────────
                        Leurs listes de valeurs viennent du SERVEUR (années, formations,
                        régimes, statuts) : une copie dans cet écran finirait par ne plus
                        correspondre au CHECK de la base, et c'est la base qui refuse en
                        dernier. */}
                    <div className="etu-filtres">
                        <span className="etu-filtre-label">Année</span>
                        {/* Changer d'année RECHARGE la fiche : le garde-fou passe par ici. */}
                        <select
                            value={anneeFiltre || ''}
                            onChange={(ev) => {
                                const choisie = ev.target.value ? Number(ev.target.value) : '';
                                demanderNavigation(() => setAnneeFiltre(choisie));
                            }}
                        >
                            {!anneesFiltre.length && <option value="">—</option>}
                            {anneesFiltre.map((a) => (
                                <option key={a.id} value={a.id}>
                                    {a.libelle}
                                    {a.nb_inscrits !== null && a.nb_inscrits !== undefined
                                        ? ` (${a.nb_inscrits})` : ''}
                                </option>
                            ))}
                        </select>

                        <div className="etu-filtre-sep" />
                        <span className="etu-filtre-label">Formation</span>
                        <select
                            value={filtres.sousNiveauId}
                            onChange={(ev) => setFiltres((p) => ({ ...p, sousNiveauId: ev.target.value }))}
                        >
                            <option value="">Toutes</option>
                            {(liste?.formations || []).map((f) => (
                                <option key={f.id} value={f.id}>
                                    {f.nom} ({f.nb_inscrits})
                                </option>
                            ))}
                        </select>

                        <div className="etu-filtre-sep" />
                        <span className="etu-filtre-label">Régime</span>
                        <select
                            value={filtres.regime}
                            onChange={(ev) => setFiltres((p) => ({ ...p, regime: ev.target.value }))}
                        >
                            <option value="">Tous</option>
                            {(liste?.regimes || []).map((r) => (
                                <option key={r.cle} value={r.cle}>{r.libelle}</option>
                            ))}
                        </select>

                        {/* 2026-10-29 (E.4) : le parcours de l'année (Programme) — affiché seulement s'il en existe. */}
                        {(liste?.parcours || []).length > 0 && (<>
                            <div className="etu-filtre-sep" />
                            <span className="etu-filtre-label">Parcours</span>
                            <select
                                value={filtres.parcours}
                                onChange={(ev) => setFiltres((p) => ({ ...p, parcours: ev.target.value }))}
                            >
                                <option value="">Tous</option>
                                <option value="sans">Tronc commun / sans parcours</option>
                                {liste.parcours.map((pc) => (
                                    <option key={pc.id} value={pc.code}>{pc.code} ({pc.nb_inscrits})</option>
                                ))}
                            </select>
                        </>)}

                        <div className="etu-filtre-sep" />
                        <span className="etu-filtre-label">Statut</span>
                        {/* Quatre valeurs, pas cinq : écart E30, décidé à ce lot.
                            « Redoublant » n'est plus un statut mais un fait annuel, affiché
                            en badge à côté du statut dans la liste. */}
                        <select
                            value={filtres.statut}
                            onChange={(ev) => setFiltres((p) => ({ ...p, statut: ev.target.value }))}
                        >
                            <option value="">Tous</option>
                            {(liste?.statuts || []).map((s) => (
                                <option key={s.cle} value={s.cle}>{s.libelle}</option>
                            ))}
                        </select>

                        <div className="etu-filtre-sep" />
                        <span className="etu-filtre-label">Boursier</span>
                        <select
                            value={filtres.boursier}
                            onChange={(ev) => setFiltres((p) => ({ ...p, boursier: ev.target.value }))}
                        >
                            <option value="">Tous</option>
                            <option value="oui">Oui</option>
                            <option value="non">Non</option>
                        </select>

                        <div className="etu-recherche">
                            <i className="fa-solid fa-magnifying-glass" />
                            <input
                                type="text"
                                placeholder="Nom, prénom, n° étudiant…"
                                value={rechercheSaisie}
                                onChange={(ev) => setRechercheSaisie(ev.target.value)}
                            />
                        </div>

                        {filtresActifs && (
                            <button
                                className="etu-btn-filtres"
                                type="button"
                                onClick={reinitialiserFiltres}
                                title="Retirer tous les filtres sauf l'année"
                            >
                                <i className="fa-solid fa-rotate-left" /> Retirer les filtres
                            </button>
                        )}
                    </div>

                    <div className="etu-split" ref={refSplit}>
                        <div className="etu-liste-wrap" ref={refListe}>
                            <EtudiantsListe
                                lignes={erreurListe ? [] : (liste?.etudiants || [])}
                                total={liste?.total || 0}
                                totalAnnee={liste?.total_annee || 0}
                                tronquee={!!liste?.tronquee}
                                annee={liste?.annee}
                                annees={liste?.annees}
                                chargement={chargementListe && !liste}
                                erreur={erreurListe}
                                filtresActifs={filtresActifs}
                                onReinitialiser={reinitialiserFiltres}
                                selection={selection}
                                onSelectionner={(id) => demanderNavigation(() => setSelection(id))}
                            />
                        </div>

                        <button
                            className="etu-resizer"
                            ref={refResizer}
                            onMouseDown={demarrerGlissement}
                            onKeyDown={clavier}
                            type="button"
                            aria-label="Redimensionner la liste (flèches gauche et droite)"
                        />

                        <div className="etu-fiche-wrap">
                            <EtudiantFiche
                                etudiantId={selection}
                                anneeId={liste?.annee?.id || anneeFiltre || anneeId}
                                departementId={departementId}
                                modifiable={modifiable}
                                // ⚠️ Les deux rappels sont STABLES (`chargerListe` est un
                                // `useCallback`), et ce n'est pas de la cosmétique : une
                                // fonction recréée à chaque rendu faisait relire la fiche
                                // au moindre rafraîchissement de la liste, ce qui EFFAÇAIT
                                // la saisie en cours. Trouvé à la relecture à l'image.
                                onErreurPerimetre={chargerListe}
                                // Statut, boursier et redoublant sont des COLONNES de la
                                // liste : un enregistrement qui ne la rafraîchirait pas
                                // laisserait deux vérités à l'écran, à dix centimètres
                                // l'une de l'autre.
                                onEnregistre={chargerListe}
                                refActions={refFiche}
                            />
                        </div>
                    </div>
                </div>

                {/* ONGLET 2 — IMPORTS (APOGEE : lot 1.2 · SCODOC : lot 1.4) */}
                {/* Pas de `etu-panneau-defile` ici : la maquette veut DEUX colonnes qui
                    défilent chacune de son côté, pas un panneau qui défile en entier. */}
                <div className={'etu-panneau' + (onglet === 'import' ? ' active' : '')}>
                    {/* Le cadre à deux colonnes est ICI depuis le lot 1.4, et non plus
                        dans `ImportApogee` : les deux imports sont désormais deux
                        composants frères, et aucun des deux n'a à décider de la mise en
                        page de l'autre. La coquille porte déjà la structure des onglets. */}
                    <div className="etu-import-layout">
                        <ImportApogee
                            user={user}
                            config={config}
                            actif={onglet === 'import'}
                            anneeId={anneeId}
                            annees={annees}
                        />
                        {/* Lot E.3 : l'import SCODOC des ÉTUDIANTS en tête de la colonne SCODOC, celui des rangs dessous */}
                        <ImportScodoc
                            user={user}
                            config={config}
                            actif={onglet === 'import'}
                            anneeId={anneeId}
                            annees={annees}
                            avant={<ImportScodocEtudiants user={user} config={config} actif={onglet === 'import'} anneeId={anneeId} annees={annees} />}
                        />
                    </div>
                </div>

                {/* ONGLET 3 — PHOTOS SCODOC (lot 1.5) */}
                <div className={'etu-panneau' + (onglet === 'photos' ? ' active' : '')}>
                    {/* ⚠️ IL REÇOIT LA LISTE, IL NE LA CHARGE PAS. `liste` est déjà
                        détenue ici pour l'onglet 1 et rechargée quand l'onglet change
                        (l'effet dépend de `onglet`). En redemander une copie aurait fait
                        deux lectures du même périmètre, et deux comptes qui finissent par
                        diverger.

                        C'est aussi ce qui réalise « la grille suit les filtres de
                        l'onglet 1 » (spec) sans une ligne de code : les filtres sont ici.

                        ⚠️ `etu-panneau-defile` est RETIRÉ : c'est la grille du
                        trombinoscope qui défile, pas le panneau. Sinon la zone d'import
                        descend hors de l'écran dès la cinquantième vignette. */}
                    <PhotosScodoc
                        lignes={erreurListe ? [] : (liste?.etudiants || [])}
                        chargement={chargementListe}
                        erreur={erreurListe}
                        annee={liste?.annee || null}
                        formations={liste?.formations || []}
                        filtres={filtres}
                        departementId={departementId}
                        anneeId={liste?.annee?.id || anneeFiltre || anneeId}
                        // Le rappel est STABLE (`chargerListe` est un `useCallback`) :
                        // un rappel recréé à chaque rendu relancerait la lecture en
                        // boucle, et le lot 1.3 bis a déjà payé ce piège.
                        onImporte={chargerListe}
                    />
                </div>

                {/* ONGLET 4 — PARAMÉTRAGE (lot 1.1) */}
                <div className={'etu-panneau etu-panneau-defile' + (onglet === 'param' ? ' active' : '')}>
                    {/* `actif` conditionne l'appel réseau : les quatre panneaux sont montés en
                        permanence, et interroger le serveur pour un onglet que l'utilisateur
                        n'ouvrira peut-être jamais est un appel pour rien. */}
                    <EtudiantsParametrage user={user} config={config} actif={onglet === 'param'} />
                </div>

            </div>

            {/* ── MODIFICATIONS NON ENREGISTRÉES ─────────────────────────────
                Une modale STYLÉE, jamais `confirm()` (règle 6). Trois issues, et la
                troisième compte autant que les deux autres : « Annuler » ramène sur la
                fiche, là où la saisie est encore là. Sans elle, se tromper de bouton
                serait une perte définitive. */}
            {navigationEnAttente && (
                <div className="etu-modale-fond" role="dialog" aria-modal="true"
                     aria-labelledby="etu-modale-titre">
                    <div className="etu-modale">
                        <div className="etu-modale-entete">
                            <i className="fa-solid fa-triangle-exclamation" />
                            <h3 id="etu-modale-titre">Modifications non enregistrées</h3>
                        </div>
                        <div className="etu-modale-corps">
                            <p>
                                Cette fiche porte des modifications qui ne sont pas encore en base —
                                une valeur corrigée, un cadenas ouvert ou fermé.
                            </p>
                            <p className="etu-modale-note">
                                Quitter la fiche sans enregistrer les perd. Un cadenas fermé et non
                                enregistré ne protège rien : le prochain import écraserait la valeur.
                            </p>
                        </div>
                        <div className="etu-modale-pied">
                            <button
                                type="button"
                                className="etu-btn-modale etu-btn-modale-primaire"
                                onClick={async () => {
                                    const geste = navigationEnAttente;
                                    const ok = await refFiche.current?.enregistrer();
                                    setNavigationEnAttente(null);
                                    // L'enregistrement a échoué : on RESTE sur la fiche, où le
                                    // bandeau rouge dit pourquoi. Naviguer quand même perdrait
                                    // la saisie ET le message qui l'explique.
                                    if (ok) geste();
                                }}
                            >
                                <i className="fa-solid fa-check" /> Enregistrer puis continuer
                            </button>
                            <button
                                type="button"
                                className="etu-btn-modale etu-btn-modale-danger"
                                onClick={() => {
                                    const geste = navigationEnAttente;
                                    setNavigationEnAttente(null);
                                    geste();
                                }}
                            >
                                <i className="fa-solid fa-trash-can" /> Abandonner les modifications
                            </button>
                            <button
                                type="button"
                                className="etu-btn-modale etu-btn-modale-secondaire"
                                onClick={() => setNavigationEnAttente(null)}
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Etudiants;
